import React, { useState } from 'react';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { openDillToast } from '../../../../utils/helpers';
import { CreditApplicationFieldsValues } from '@dill/dill-shared';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { UploadFileInputs } from './UploadFileInput';
import moment from 'moment';
import { useAppSelector } from '../../../../redux/store';
import { X } from 'phosphor-react';

const TradeReferenceResponseModal = ({
  open,
  handleClose,
  fields,
  index,
  onUploadTradeReferenceResponse,
}: {
  open: boolean;
  handleClose: () => void;
  fields: CreditApplicationFieldsValues[];
  index: number;
  onUploadTradeReferenceResponse?: (data: {
    files: { value: File; id: string }[];
    text: { value: string; id: string } | null;
    fields: CreditApplicationFieldsValues[];
  }) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [responseText, setResponseText] = useState<{ value: string; id: string } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ value: File; id: string }[]>([]);
  const { user } = useAppSelector((state) => state.auth);

  if (!fields || fields.length === 0 || !fields[index]) {
    return null;
  }

  const uniqueId = fields[index]?.uniqueId ?? '';
  const currentResponseText =
    responseText && responseText.id === uniqueId ? responseText.value : null;
  const currentFiles = selectedFiles.filter((f) => f.id === uniqueId);

  const handleCloseModal = () => {
    setLoading(false);
    setResponseText(null);
    setSelectedFiles([]);
    handleClose();
  };
  const handleSubmit = () => {
    const text = responseText && responseText.id === uniqueId ? responseText : null;

    if ((!text || !text.value.trim()) && currentFiles.length === 0) {
      openDillToast({
        message: 'Please provide either a trade reference text or upload at least one file.',
        type: 'ERROR',
      });
      return;
    }

    const updatedFields = fields.map((f) => {
      return {
        ...f,
        status: 'Submitted' as const,
        referenceHistoryDate: [
          ...(f.referenceHistoryDate ?? []),
          {
            submitted: moment().format('MM-DD-YYYY'),
            actionBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
            action: 'submitted',
            note: 'Submitted a manual trade reference response.',
            date: moment().format('MM-DD-YYYY LT'),
          },
        ],
      };
    });

    if (onUploadTradeReferenceResponse) {
      onUploadTradeReferenceResponse({ files: currentFiles, text, fields: updatedFields });
      handleCloseModal();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Manual Trade Reference Response"
      closeOnOverlayClick={true}
      contentClassName="flex flex-col gap-3.5 overflow-hidden"
      footer={
        <div className="flex justify-end gap-2">
          <DillButton label="Close" onClick={handleCloseModal} variant="secondary" />
          <DillButton
            label="Submit"
            onClick={() => {
              handleSubmit();
            }}
            isLoading={loading}
            isDisabled={loading}
            variant="primary"
          />
        </div>
      }
      maxWidth="600px">
      <div className="flex flex-col gap-2 md:gap-2 p-4">
        <AppInputField
          label="Trade Reference"
          id="tradeReference"
          placeholder="Enter Trade Reference Response"
          inputType="textarea"
          value={currentResponseText ?? ''}
          onTextChange={(text) => setResponseText({ id: uniqueId, value: text })}
        />
        {currentFiles.map((file, idx) => (
          <div
            key={'trade-reference-uploaded-files-' + idx}
            className="flex gap-1 md:gap-2 items-center">
            <div className="text-xs">{file.value.name}</div>
            <button
              onClick={() => {
                setSelectedFiles((prev) => prev.filter((f) => f.id !== file.id));
              }}
              className="text-red-500 text-xs flex items-center gap-1">
              <X size={14} />
              Remove
            </button>
          </div>
        ))}
        <UploadFileInputs
          label=""
          selectedFile={null}
          onSelectFile={(data) =>
            setSelectedFiles((prev) => [...prev, { id: uniqueId, value: data.file }])
          }
        />
      </div>
    </DillModal>
  );
};

export default TradeReferenceResponseModal;
